/**
 * Nmbrs Leave Hooks
 *
 * React Query hooks for leave balance and leave list data from Nmbrs.
 * Story 3.1: Verlofsaldo Sync
 * Story 3.2: Verlofoverzicht
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SyncStatus } from '@functieloonhuis/shared';
import { apiRequest } from '../../../lib/api';

// ============================================
// Types
// ============================================

export interface NmbrsLeaveBalance {
  employeeId: number;
  type: string;
  /** Beginstand uit Nmbrs; `null` als de saldo-call faalde. Nog niet getoond. */
  openingBalance: number | null;
  /** Opgenomen uren in `year` — alleen GOEDGEKEURDE opnames. */
  used: number;
  /**
   * Goedgekeurde uren die nog VOOR je liggen (start na vandaag) — geboekt,
   * maar nog niet opgenomen. Nmbrs heeft dit al van het saldo afgetrokken, dus
   * nooit nog een keer aftrekken; het staat er zodat je ziet wat al vastligt.
   */
  planned: number;
  /**
   * Het saldo van VANDAAG, rechtstreeks uit Nmbrs (`CurrentBalance`). De
   * Nmbrs-call kent geen jaar, dus dit volgt de jaarkiezer niet; `used` wel.
   * Mag echt negatief zijn — dan loop je voor op je opbouw.
   *
   * `null` betekent ONBEKEND, niet nul. Nooit als 0 meetellen in een totaal,
   * een teller of een sortering: precies dat gedrag hield de saldobug
   * maandenlang onzichtbaar.
   */
  remaining: number | null;
  year: number;
  /**
   * Door Nmbrs vervallen uren in `year`. Informatief — Nmbrs heeft het verval
   * al in `remaining` verwerkt, dus nóg een keer aftrekken telt dubbel.
   */
  expired: number;
  /** Jaarrecht in uren; `null` bij kort verzuim/TvT of zonder contracturen. */
  yearEntitlement: number | null;
  /**
   * Wat je dit jaar nog kunt plannen = saldo van vandaag + de opbouw die nog
   * komt. Dít beantwoordt "kan ik in juli drie weken weg?"; het saldo alleen
   * leest in februari als een weigering. `null` = onbekend, nooit 0.
   */
  yearRemaining: number | null;
  /**
   * Het saldo waarmee deze medewerker het jaar BEGON, in uren: wat er van
   * vorig jaar meekwam plus het jaarrecht.
   *
   * Maakt de rekensom op het scherm controleerbaar:
   * `beginsaldo − opgenomen − gepland − vervallen = nog te plannen`.
   *
   * LET OP: die som klopt niet voor iedereen. Nmbrs' saldo is niet altijd de
   * som van zijn onderdelen (gemeten 2026-07-30). Het jaarcijfer blijft daarom
   * op het Nmbrs-saldo gebaseerd; dit veld is er om de som te tonen, en de UI
   * markeert de rijen waar de twee uiteenlopen.
   */
  yearStartBalance: number | null;
  /** Gemiddelde uren per werkdag — de basis om dagen te tonen in plaats van uren. */
  hoursPerDay: number | null;
  /** Week 1 wijkt af van week 2; het dagental is dan een gemiddelde ("± N"). */
  varyingSchedule: boolean;
  /** De ingestelde vakantiedagen per jaar — zodat het scherm de rekenbasis kan noemen. */
  entitlementBasisDays: number | null;
}

interface LeaveBalancesResponse {
  data: NmbrsLeaveBalance[];
}

interface EmployeeBalanceResult {
  employeeId: number;
  employeeName: string;
  balances: NmbrsLeaveBalance[];
}

interface EmployeeBalanceError {
  employeeId: number;
  employeeName: string;
  error: string;
}

interface AllLeaveBalancesResponse {
  data: EmployeeBalanceResult[];
  errors: EmployeeBalanceError[];
  /**
   * Medewerkers die WEL in scope zitten maar geen Nmbrs-koppeling hebben. Ze
   * hebben geen saldo, maar een teamtotaal dat hen stil weglaat is een schoon
   * getal dat niet klopt — daarom apart geteld.
   */
  unlinkedCount: number;
  /** Door de SERVER bepaalde scope. De UI labelt hierop; een prop kan liegen. */
  scope: 'org' | 'team';
}

// ============================================
// Hooks
// ============================================

/**
 * Hook to fetch leave balances for a single employee.
 *
 * @param employeeId - Employee ID to fetch balances for
 * @param year - Optional year filter (defaults to current year on server)
 */
export function useLeaveBalances(employeeId: number, year?: number) {
  const yearParam = year ? `?year=${year}` : '';

  return useQuery({
    queryKey: ['nmbrs', 'leave', 'balances', employeeId, year],
    queryFn: () =>
      apiRequest<LeaveBalancesResponse>(
        `/nmbrs/leave/balances/${employeeId}${yearParam}`
      ),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
    enabled: employeeId > 0,
  });
}

// ============================================
// Story 3.2: Leave List Types & Hook
// ============================================

export interface NmbrsLeaveRecord {
  id: number;
  employeeId: number;
  type: string;
  startDate: string;
  endDate: string;
  hours: number;
  status: string;
  description: string;
}

interface LeaveListResponse {
  data: {
    leaves: NmbrsLeaveRecord[];
    hoursPerDay: number;
  };
}

/**
 * Hook to fetch leave records (withdrawals) for a single employee.
 * Story 3.2: Verlofoverzicht
 *
 * @param employeeId - Employee ID to fetch leave list for
 * @param year - Optional year filter (defaults to current year on server)
 */
export function useLeaveList(employeeId: number, year?: number) {
  const yearParam = year ? `?year=${year}` : '';

  return useQuery({
    queryKey: ['nmbrs', 'leave', 'list', employeeId, year],
    queryFn: () =>
      apiRequest<LeaveListResponse>(
        `/nmbrs/leave/list/${employeeId}${yearParam}`
      ),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
    enabled: employeeId > 0,
  });
}

/**
 * Hook to fetch leave balances for all linked employees (batch).
 *
 * @param year - Optional year filter (defaults to current year on server)
 */
export function useAllLeaveBalances(year?: number) {
  const yearParam = year ? `?year=${year}` : '';

  return useQuery({
    queryKey: ['nmbrs', 'leave', 'balances', 'all', year],
    queryFn: () =>
      apiRequest<AllLeaveBalancesResponse>(
        `/nmbrs/leave/balances${yearParam}`
      ),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
  });
}

// ============================================
// Story 3.5: Absence (Verzuim) Types & Hooks
// ============================================

export interface NmbrsAbsence {
  id: number;
  employeeId: number;
  startDate: string;
  endDate?: string;
  percentage: number;
  type: string;
}

export interface AbsenceSummaryItem {
  employeeId: number;
  employeeName: string;
  startDate: string;
  endDate: string | null;
  percentage: number;
  type: string;
  contractHours: number | null;
  absenceDays: number | null;
}

export interface AbsenceSummary {
  currentlyAbsent: number;
  totalLinked: number;
  absences: AbsenceSummaryItem[];
}

interface AbsenceListResponse {
  data: NmbrsAbsence[];
}

interface AbsenceSummaryResponse {
  data: AbsenceSummary;
}

/**
 * Hook to fetch absence records for a single employee.
 * Story 3.5: Verzuimoverzicht
 *
 * @param employeeId - Employee ID to fetch absences for
 */
export function useAbsenceList(employeeId: number) {
  return useQuery({
    queryKey: ['nmbrs', 'absence', employeeId],
    queryFn: () =>
      apiRequest<AbsenceListResponse>(
        `/nmbrs/leave/absence/${employeeId}`
      ),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
    enabled: employeeId > 0,
  });
}

/**
 * Hook to fetch team-wide absence summary.
 * Story 3.5: Verzuimoverzicht
 *
 * @param includeAll - If true, includes closed absences too (not just open)
 */
export function useAbsenceSummary(includeAll: boolean = false) {
  const param = includeAll ? '?includeAll=true' : '';
  return useQuery({
    queryKey: ['nmbrs', 'absence', 'summary', includeAll],
    queryFn: () =>
      apiRequest<AbsenceSummaryResponse>(
        `/nmbrs/leave/absence/summary${param}`
      ),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
  });
}

// ============================================
// Story 3.4: Team Leave Calendar Types & Hooks
// ============================================

export interface TeamLeaveItem {
  id: number;
  type: string;
  leaveType: number;
  startDate: string;
  endDate: string;
  hours: number;
  status: string;
  description: string;
}

export interface TeamLeaveEntry {
  employeeId: number;
  employeeName: string;
  nmbrsEmployeeId: number;
  leaves: TeamLeaveItem[];
}

interface TeamLeaveResponse {
  data: TeamLeaveEntry[];
}

/**
 * Hook to fetch team leave calendar data for a date range.
 * Story 3.4: Team Verlofkalender
 *
 * @param startDate - ISO date string (YYYY-MM-DD)
 * @param endDate - ISO date string (YYYY-MM-DD)
 */
export function useTeamLeave(startDate: string, endDate: string) {
  return useQuery({
    queryKey: ['nmbrs', 'leave', 'team', startDate, endDate],
    queryFn: () =>
      apiRequest<TeamLeaveResponse>(
        `/nmbrs/leave/team?startDate=${startDate}&endDate=${endDate}`
      ),
    staleTime: 5 * 60 * 1000, // 5 min
    retry: 1,
    enabled: !!startDate && !!endDate,
  });
}

// ============================================
// Holidays Hook
// ============================================

export interface HolidayItem {
  id: number;
  name: string;
  date: string;
  year: number;
}

interface HolidaysResponse {
  data: HolidayItem[];
}

/**
 * Hook to fetch public holidays for a given year.
 *
 * @param year - The year to fetch holidays for
 */
export function useHolidays(year: number) {
  return useQuery({
    queryKey: ['holidays', year],
    queryFn: () =>
      apiRequest<HolidaysResponse>(`/settings/holidays?year=${year}`),
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: year > 0,
  });
}

// ============================================
// Story 3.3: Leave Request Types & Hooks
// ============================================

export interface LeaveRequestResult {
  id: number;
  employeeId: number;
  nmbrsEmployeeId: number;
  leaveType: number;
  leaveTypeName: string;
  startDate: string;
  endDate: string;
  hours: number;
  description: string | null;
  nmbrsLeaveId: number | null;
  /**
   * Story 9.1, AC-11 — de volledige boek-as, gelijk aan het type dat de
   * backend teruggeeft. `'niet_boekend'` hoort er sinds die story bij; onder
   * het oude, smallere type viel die waarde in de mislukt-tak van
   * `SyncStatusBadge` en kreeg verlof dat nooit boekt een rode badge (FR-23).
   */
  syncStatus: SyncStatus;
  syncError: string | null;
  submittedBy: number;
  submittedAt: string;
  syncedAt: string | null;
}

interface LeaveRequestsResponse {
  data: LeaveRequestResult[];
}

interface LeaveRequestResponse {
  data: LeaveRequestResult;
}

export interface SubmitLeaveRequestInput {
  employeeId: number;
  leaveType: number;
  startDate: string;
  endDate: string;
  hours: number;
  description?: string;
}

/**
 * Hook to submit a leave request to Nmbrs.
 * Invalidates leave queries on success.
 * Story 3.3: Verlofaanvraag Indienen
 */
export function useSubmitLeaveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SubmitLeaveRequestInput) =>
      apiRequest<LeaveRequestResponse>('/nmbrs/leave/request', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'leave'] });
    },
  });
}

/**
 * Hook to fetch local leave requests for an employee.
 * Story 3.3: Verlofaanvraag Indienen
 */
export function useLocalLeaveRequests(employeeId: number) {
  return useQuery({
    queryKey: ['nmbrs', 'leave', 'requests', employeeId],
    queryFn: () =>
      apiRequest<LeaveRequestsResponse>(`/nmbrs/leave/requests/${employeeId}`),
    staleTime: 2 * 60 * 1000, // 2 min
    retry: 1,
    enabled: employeeId > 0,
  });
}

/**
 * Hook to delete a leave request.
 * Story 3.3: Verlofaanvraag Indienen
 */
export function useDeleteLeaveRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) =>
      apiRequest<void>(`/nmbrs/leave/request/${requestId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'leave'] });
    },
  });
}

/**
 * Hook to retry a failed leave request sync.
 * Story 3.3: Verlofaanvraag Indienen
 */
export function useRetryLeaveSync() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) =>
      apiRequest<LeaveRequestResponse>(`/nmbrs/leave/request/${requestId}/retry`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'leave'] });
    },
  });
}

/**
 * Story 2.3 (Epic 2, Verlof correctie) — uitkomst van de terugzoekactie over
 * de hele historie. Zelfde velden als `LinkAllReport` op de backend
 * (`nmbrsLeaveService.ts`) — bewust lokaal gedefinieerd, niet in
 * `@functieloonhuis/shared`, zelfde conventie als de rest van dit bestand
 * (bijv. `LeaveRequestResponse` hierboven).
 */
export interface NmbrsLeaveLinkAllReport {
  gekoppeld: number;
  nietGekoppeld: number;
  uitgevoerdOp: string;
}

interface NmbrsLeaveLinkAllResponse {
  data: NmbrsLeaveLinkAllReport;
}

interface NmbrsLeaveLinkAllStatusResponse {
  data: NmbrsLeaveLinkAllReport | null;
}

/**
 * Story 2.3 — de laatst uitgevoerde koppelactie, voor de uitkomstregel
 * "Laatst uitgevoerd: ..." (AC-3). `null` zolang de actie nog nooit gedraaid
 * heeft.
 */
export function useNmbrsLeaveLinkStatus() {
  return useQuery({
    queryKey: ['nmbrs', 'leave', 'link-all', 'status'],
    queryFn: () => apiRequest<NmbrsLeaveLinkAllStatusResponse>('/nmbrs/leave/link-all/status'),
    select: (res) => res.data,
    staleTime: 2 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Story 2.3 — start de terugzoekactie over de hele historie
 * (`POST /nmbrs/leave/link-all`). Geen voortgangsbalk, geen tussenstand
 * (AC-3): dit is één aanroep die pas ná afloop het rapport teruggeeft.
 * Invalideert de verlofoverzichten en de "laatst uitgevoerd"-status, zodat
 * FR-7 (buiten scope van deze story) later op verse data kan bouwen.
 */
export function useLinkAllLeaveRequests() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiRequest<NmbrsLeaveLinkAllResponse>('/nmbrs/leave/link-all', {
        method: 'POST',
      }).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nmbrs', 'leave'] });
    },
  });
}
